import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { useRequest, useSetState } from 'ahooks';
import { App, Button, DatePicker, Flex, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { mapValues } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import OperationLogDrawer from '@/components/OperationLogDrawer';
import { logs } from '@/services/logs';

/**
 * Types
 */
import type { ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';
import type { LogsParams, LogsResponse } from '@/services/logs';

type State = {
    dataSource: LogsResponse[];
};

const defaultPayload: LogsParams = {
    account_id: undefined,
    action: undefined,
    module: undefined,
    start_time: undefined,
    end_time: undefined,
    status: 'Success',
};

const actionOptions = [
    { label: 'Create', value: 'Create' },
    { label: 'Delete', value: 'Delete' },
    { label: 'Export', value: 'Export' },
    { label: 'Login', value: 'Login' },
    { label: 'Logout', value: 'Logout' },
    { label: 'Other', value: 'Other' },
    { label: 'Update', value: 'Update' },
];

const PageLogs: React.FC = () => {
    const { message } = App.useApp();
    const [formSearch] = Form.useForm();
    const { formatMessage } = useIntl();
    const searchRef = useRef<HTMLDivElement>(null);

    const [state, setState] = useSetState<State>({
        dataSource: [],
    });

    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        total: 0,
        onChange: (page, pageSize) => {
            setPagination({
                current: page,
                pageSize,
            });
        },
    });

    const [payload, setPayload] = useSetState<LogsParams>({
        ...defaultPayload,
        end_time: dayjs().endOf('d').toISOString(),
        start_time: dayjs().startOf('d').toISOString(),
    });
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logBizId, setLogBizId] = useState<string | null>(null);

    const {
        loading: loadingLogs,
        refresh: refreshLogs,
        run: runLogs,
    } = useRequest(logs, {
        manual: true,
        onSuccess: ({ data: res }) => {
            const { code, data, meta, msg } = res as { code: number; data: LogsResponse[]; meta?: { page?: number; size?: number; total?: number }; msg: string };
            if (code !== 200) {
                return message.error(msg);
            }

            const list = Array.isArray(data) ? data : [];
            setPagination((prev) => ({
                ...prev,
                current: meta?.page ?? prev.current,
                pageSize: meta?.size ?? prev.pageSize,
                total: meta?.total ?? list.length,
            }));

            setState({
                dataSource: list,
            });
        },
    });

    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: {
            status: 'Success',
            operation_date: [dayjs().startOf('d'), dayjs().endOf('d')],
        },
        layout: 'inline',
        onFinish: (values) => {
            if (values.operation_date?.length === 2) {
                // 接口要求 ISO datetime 且以 Z 结尾（UTC），不能带 +08:00 等偏移
                values.end_time = values.operation_date[1].endOf('d').toISOString();
                values.start_time = values.operation_date[0].startOf('d').toISOString();
            }
            values.operation_date = undefined;

            setPagination((prev) => ({ ...prev, current: 1 }));

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };
            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<LogsResponse, any> = {
        bordered: true,
        columns: [
            {
                dataIndex: 'index',
                fixed: 'left',
                title: 'No.',
                valueType: 'index',
                width: 50,
            },
            {
                dataIndex: 'ip',
                title: 'IP',
                width: 120,
            },
            {
                dataIndex: 'action',
                title: <FormattedMessage id="t.c.type" />,
                width: 100,
            },
            {
                dataIndex: 'module',
                ellipsis: true,
                title: <FormattedMessage id="p.logs.t.c.menu" />,
                width: 120,
            },
            {
                dataIndex: 'path',
                ellipsis: true,
                title: 'Path',
                width: 180,
            },
            {
                copyable: true,
                dataIndex: 'body',
                ellipsis: true,
                title: <FormattedMessage id="p.logs.t.c.body" />,
                renderText: (text) => text != null && JSON.stringify(text),
            },
            {
                dataIndex: 'status',
                title: <FormattedMessage id="p.logs.t.c.result" />,
                width: 100,
            },
            {
                dataIndex: 'account_id',
                ellipsis: true,
                title: <FormattedMessage id="t.c.operatedBy" />,
                width: 140,
            },
            {
                dataIndex: 'created_at',
                title: <FormattedMessage id="t.c.operatedAt" />,
                valueType: 'dateTime',
                width: 180,
            },
            {
                fixed: 'right',
                key: 'option',
                title: <FormattedMessage id="t.c.action" />,
                valueType: 'option',
                width: 100,
                render: (_, record) => (
                    <Button
                        type="link"
                        onClick={() => {
                            setLogBizId(record.id);
                            setLogDrawerOpen(true);
                        }}
                    >
                        操作记录
                    </Button>
                ),
            },
        ],
        dataSource: state.dataSource,
        loading: loadingLogs,
        options: {
            reload: refreshLogs,
        },
        pagination,
        rowKey: 'id',
        scroll: {
            x: 1400,
            y: `calc(100vh - 224px - ${searchRef.current?.clientHeight || 64}px - 16px - 48px)`,
        },
        search: false,
    };

    useEffect(() => {
        runLogs({
            ...payload,
            page: pagination.current,
            size: pagination.pageSize,
        });
    }, [pagination.current, pagination.pageSize, payload]);

    return (
        <>
            <ProCard className="mb-16" ref={searchRef}>
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap={true}>
                        <Form.Item name="action" style={{ marginRight: 0 }}>
                            <Select
                                allowClear
                                optionFilterProp="label"
                                options={actionOptions}
                                placeholder={<FormattedMessage id="t.c.type" />}
                                showSearch
                                style={{ width: 140 }}
                            />
                        </Form.Item>

                        <Form.Item name="status" style={{ marginRight: 0 }}>
                            <Select
                                allowClear
                                options={[
                                    { label: 'Success', value: 'Success' },
                                    { label: 'Failure', value: 'Failure' },
                                    { label: 'Init', value: 'Init' },
                                ]}
                                placeholder={<FormattedMessage id="p.logs.t.c.result" />}
                                style={{ width: 120 }}
                            />
                        </Form.Item>

                        <Form.Item name="account_id" style={{ marginRight: 0 }}>
                            <Input placeholder={<FormattedMessage id="t.c.operatedBy" />} style={{ width: 160 }} />
                        </Form.Item>

                        <Form.Item label={formatMessage({ id: 't.c.operatedAt' })} name="operation_date" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear />
                        </Form.Item>

                        <Button
                            loading={loadingLogs}
                            type="primary"
                            onClick={() => formSearch.submit()}
                        >
                            <FormattedMessage id="b.search" />
                        </Button>

                        <Button onClick={() => formSearch.resetFields()}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <AdminProTable {...proTableProps} />
            <OperationLogDrawer
                open={logDrawerOpen}
                bizId={logBizId}
                onClose={() => {
                    setLogDrawerOpen(false);
                    setLogBizId(null);
                }}
            />
        </>
    );
};

export default PageLogs;
